<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Trash2, Monitor, Tablet, Smartphone, Globe } from 'lucide-vue-next'
import type { Session } from '@/types/client'

const props = defineProps<{
  sessions: Session[]
  showActions?: boolean
  highlightCurrent?: boolean
  title?: string
  onLogout?: (sessionId: number) => void
  formatDate: (d: any) => string
}>()

function getDeviceIcon(userAgent: string) {
  const ua = userAgent.toLowerCase()
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return Smartphone
  if (ua.includes('tablet') || ua.includes('ipad')) return Tablet
  return Monitor
}
</script>

<template>
  <div v-if="props.sessions.length > 0" class="mb-6">
    <h3
      v-if="props.title"
      class="text-[10px] font-bold mb-3 text-gray-400 uppercase tracking-widest pl-1"
    >
      {{ props.title }}
    </h3>

    <div class="overflow-hidden rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table class="w-full text-sm text-left border-collapse table-fixed">
        <thead class="bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 text-gray-500">
          <tr>
            <th class="py-2.5 px-4 font-bold text-[10px] uppercase tracking-wider w-[50%]">Device</th>
            <th class="py-2.5 px-4 font-bold text-[10px] uppercase tracking-wider w-[25%]">Location</th>
            <th class="py-2.5 px-4 font-bold text-[10px] uppercase tracking-wider w-[25%] text-right">
               {{ props.showActions ? 'Active / Action' : 'Last Active' }}
            </th>
          </tr>
        </thead>
        <TransitionGroup name="list" tag="tbody">
          <tr
            v-for="session in props.sessions"
            :key="session.id"
            :class="[
              'group border-b last:border-0 border-gray-50 dark:border-gray-800 transition-colors',
              props.highlightCurrent ? 'bg-primary/5 dark:bg-primary/20' : 'hover:bg-gray-50/50 dark:hover:bg-white/5'
            ]"
          >
            <td class="py-3 px-4 overflow-hidden">
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm flex-shrink-0">
                  <component :is="getDeviceIcon(session.userAgent)" class="w-3.5 h-3.5 text-gray-400" />
                </div>
                <div class="flex flex-col min-w-0">
                  <span :class="[
                    'font-bold truncate text-xs',
                    props.highlightCurrent ? 'text-black dark:text-white' : 'text-gray-700 dark:text-gray-200'
                  ]">{{ session.userAgent || 'Unknown device' }}</span>
                  <span class="text-[9px] text-gray-400 truncate">Created: {{ props.formatDate(session.createdAt) }}</span>
                </div>
              </div>
            </td>
            <td class="py-3 px-4">
              <div class="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <Globe class="w-3 h-3 opacity-40" />
                <span class="font-mono text-[10px] truncate">{{ session.ipAddress || '—' }}</span>
              </div>
            </td>
            <td class="py-3 px-4 text-right">
               <div class="flex items-center justify-end gap-3">
                  <span class="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {{ props.formatDate(session.lastActiveAt) }}
                  </span>
                  <Button
                    v-if="props.showActions"
                    variant="ghost"
                    size="sm"
                    class="text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg h-7 w-7 p-0 max-md:px-0 flex-shrink-0"
                    @click="props.onLogout?.(session.id)"
                  >
                    <Trash2 class="w-5 h-5" />
                  </Button>
               </div>
            </td>
          </tr>
        </TransitionGroup>
      </table>
    </div>
  </div>

  <div v-else-if="props.title" class="py-8 text-center rounded-xl border-2 border-dashed border-gray-100 dark:border-gray-800">
    <Globe class="w-5 h-5 text-gray-200 dark:text-gray-800 mx-auto mb-2" />
    <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">No {{ props.title.toLowerCase() }} Found</span>
  </div>
</template>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.list-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.list-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>